/**
 * Building blocks shared between the desktop secondary menu and the mobile
 * menu, so the two stay visually identical instead of drifting apart from
 * copy-pasted markup. Both menus show the same "Visit Bethel / Weekly
 * Gatherings / Explore / Connect" content - only the surrounding layout
 * (a 4-column dropdown vs. a single scrollable column) differs.
 */
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupsIcon from '@mui/icons-material/Groups';
import { FaHandsPraying } from 'react-icons/fa6';
import { NavLink } from 'react-router-dom';
import {
  prayerNightShortString,
  bibleStudyShortString,
  youthNightShortString,
  privacyPolicyString,
  privacyPolicyPath,
  refundPolicyString,
  refundPolicyPath,
} from '../constants';
import bbcCrest from '../assets/logos/bbc_crest.png';
import romanianFlag from '../footer/flags/romania.svg';
import americanFlag from '../footer/flags/united-states-of-america.svg';

export const ADDRESS_MAPS_URL = 'https://maps.app.goo.gl/eGUZqK5ZFA1fdj4NA';

export const MIDWEEK_ICONS = {
  2: <FaHandsPraying size={17} />,
  4: <MenuBookIcon sx={{ fontSize: '20px' }} />,
  5: <GroupsIcon sx={{ fontSize: '20px' }} />,
};

export const MIDWEEK_SHORT_NAMES = {
  2: prayerNightShortString,
  4: bibleStudyShortString,
  5: youthNightShortString,
};

export const Eyebrow = ({ children, theme, color }) => {
  const eyebrowColor = color || theme.palette.brand.brass;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
      <span style={{ width: '16px', height: '1px', backgroundColor: eyebrowColor }} />
      <span
        style={{
          fontFamily: theme.typography.fontFamily,
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: eyebrowColor,
        }}
      >
        {children}
      </span>
      <span style={{ width: '16px', height: '1px', backgroundColor: eyebrowColor }} />
    </div>
  );
};

export const SectionHeading = ({ children, theme }) => (
  <h3
    style={{
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: '23px',
      fontWeight: 600,
      color: theme.palette.primary.dark,
      margin: '0 0 14px',
    }}
  >
    {children}
  </h3>
);

/**
 * The "10:00 AM · 6:00 PM" Sunday worship times. Two fixes bundled here so
 * every place showing this stays consistent:
 *  - font-variant-numeric: lining-nums, because Cormorant Garamond's
 *    default (old-style) figures make digits like "1" and "0" render at
 *    different sizes/baselines within the same number - reads as messy at
 *    this size, not a font pairing issue.
 *  - the separating dot is deliberately larger and Brass-colored so the
 *    two times read as two distinct services, not a single "10 to 6" span.
 */
export const SundayTimesDisplay = ({ theme, style }) => (
  <div style={{ fontVariantNumeric: 'lining-nums', lineHeight: 1.15, ...style }}>
    <span>10:00 AM</span>
    <span style={{ color: theme.palette.brand.brass, fontSize: '1.3em', fontWeight: 700, margin: '0 10px', verticalAlign: 'middle' }}>
      &middot;
    </span>
    <span>6:00 PM</span>
  </div>
);

/**
 * A row with a leading icon. By default the icon sits in a filled circle
 * (the desktop secondary menu's look); pass `flat` to instead show the
 * icon directly in Reformed Navy against a transparent background - a
 * lighter-weight treatment used on mobile to keep long lists of rows from
 * feeling cluttered with big circular badges.
 */
export const IconRow = ({ icon, theme, divider, flat, children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: flat ? '14px' : '12px',
      padding: '10px 0',
      borderBottom: divider ? `1px solid ${theme.palette.brand.border}` : 'none',
    }}
  >
    <div
      style={
        flat
          ? {
              width: '22px',
              minWidth: '22px',
              color: theme.palette.primary.dark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
          : {
              width: '36px',
              height: '36px',
              minWidth: '36px',
              borderRadius: '50%',
              backgroundColor: theme.palette.brand.mist,
              color: theme.palette.primary.dark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
      }
    >
      {icon}
    </div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

/**
 * Flag-only language switcher (no "Română"/"English" text) - both flags are
 * permanently desaturated, with the inactive one further dimmed.
 */
export const LanguageSwitcher = ({ theme, language, onChangeLanguage, EN, RO, size = 24 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
    <button
      type="button"
      onClick={() => onChangeLanguage(RO)}
      aria-label="Română"
      style={{
        display: 'flex',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        filter: 'saturate(0.45)',
        opacity: language === RO ? 0.9 : 0.4,
      }}
    >
      <img src={romanianFlag} alt="Română" style={{ width: `${size}px`, height: 'auto' }} />
    </button>
    <button
      type="button"
      onClick={() => onChangeLanguage(EN)}
      aria-label="English"
      style={{
        display: 'flex',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        filter: 'saturate(0.45)',
        opacity: language === EN ? 0.9 : 0.4,
      }}
    >
      <img src={americanFlag} alt="English" style={{ width: `${size}px`, height: 'auto' }} />
    </button>
  </div>
);

/**
 * The faded-crest + Privacy/Refund Policy + copyright row shown at the
 * bottom of both menus, now that the site has no global footer. `trailing`
 * is an optional node rendered after the copyright text - the desktop
 * secondary menu uses it for the language switcher.
 */
export const MenuFooterBar = ({ theme, language, handleClick, style, trailing }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', ...style }}>
    <img src={bbcCrest} alt="" style={{ width: '32px', height: 'auto', opacity: 0.4 }} />
    <NavLink
      to={privacyPolicyPath}
      onClick={handleClick}
      style={{ fontFamily: theme.typography.fontFamily, fontSize: '13px', color: theme.palette.text.secondary, textDecoration: 'none' }}
    >
      {privacyPolicyString(language)}
    </NavLink>
    <span style={{ color: theme.palette.brand.border }}>|</span>
    <NavLink
      to={refundPolicyPath}
      onClick={handleClick}
      style={{ fontFamily: theme.typography.fontFamily, fontSize: '13px', color: theme.palette.text.secondary, textDecoration: 'none' }}
    >
      {refundPolicyString(language)}
    </NavLink>
    <span style={{ color: theme.palette.brand.border }}>|</span>
    <span style={{ fontFamily: theme.typography.fontFamily, fontSize: '13px', color: theme.palette.text.secondary }}>
      © {new Date().getFullYear()} Bethel Bible Church
    </span>
    {trailing}
  </div>
);
